// ── Modulonkénti téma ────────────────────────────────────────────────────────────────────────
// Egy modul kétféle keretben jelenhet meg:
//   KLASSZIKUS – bal oldali menüsáv (profil, intézmény- és modulváltó, menüpontok).
//   FÓKUSZ     – nincs bal sáv: felső sáv a modul ikonjával és saját főmenüjével, a profil, az
//                intézmény- és a modulváltás a jobb felső lenyílóban; a bal sáv csak mobilon nyílik.
//
// Hogy egy modul melyikben jelenik meg, az intézmény ADMINJA dönti el (feladatellatas.mode_tema,
// backend/modul-tema.js). A modul adminja a böngészőjében KI IS PRÓBÁLHATJA a másikat – az a
// próba csak nála él (localStorage), másra nincs hatása.
//
// Fókuszban csak az a modul jelenhet meg, amelyiknek elkészült a Fókusz-kerete (saját fejléc és
// menü) – a többinél a bal sáv nélkül nem lenne mivel navigálni. Új modulnál: a keret elkészülte
// után kerüljön a FOKUSZ_KEPES-be.

export type ModulTema = 'klasszikus' | 'fokusz';

export const MODUL_TEMAK: { kod: ModulTema; nev: string; leiras: string; ikon: string }[] = [
  { kod: 'klasszikus', nev: 'Klasszikus', ikon: 'browsers-outline',
    leiras: 'Bal oldali menüsáv: profil, intézmény- és modulváltó, menüpontok.' },
  { kod: 'fokusz', nev: 'Fókusz', ikon: 'scan-outline',
    leiras: 'Nincs bal sáv: felső sáv a modul ikonjával és főmenüjével, a profil jobb fent; a bal sáv csak mobilon.' },
];

/** Modulok, amelyeknek elkészült a Fókusz-kerete. A SULINFORM-é (components/sulinform-fokusz-sav)
 *  2026-10-16 óta élesben is választható; az alapértelmezése viszont KLASSZIKUS marad (nincs az
 *  ALAP_TEMA-ban) – Fókuszt csak az lát, akinek az admin kijelöli, vagy aki a Beállításokban maga
 *  választja (a user: „az alap maradjon a klasszikus téma”). */
export const FOKUSZ_KEPES = new Set(['NAPLO', 'BER', 'ESZKOZ', 'SZAMLAZO', 'INVENTARIO', 'GEPJARMU', 'SULINFORM']);

/** A Fókusz-keretes modulok kezdőlapja (pl. az adminból ide visz vissza a „vissza" gomb). */
export const MODUL_KEZDOLAP: Record<string, string> = { NAPLO: '/naplo', BER: '/ber', ESZKOZ: '/eszkoz', SZAMLAZO: '/szamlazo', INVENTARIO: '/inventario', GEPJARMU: '/gepjarmu' };

/** Alapérték, ha az admin még nem döntött. A SULINFORM szándékosan nincs benne: alapból Klasszikus. */
const ALAP_TEMA: Record<string, ModulTema> = { NAPLO: 'fokusz', BER: 'fokusz', ESZKOZ: 'fokusz', SZAMLAZO: 'fokusz', INVENTARIO: 'fokusz', GEPJARMU: 'fokusz' };

export function temaNev(kod: string | null | undefined): string {
  return MODUL_TEMAK.find((t) => t.kod === kod)?.nev || '';
}

/** A feladatellatas.mode_tema nyers értéke (JSON-szöveg vagy objektum) → objektum. */
export function temaBeallitas(nyers: any): Record<string, string> {
  if (!nyers) return {};
  try {
    const o = typeof nyers === 'string' ? JSON.parse(nyers) : nyers;
    return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  } catch { return {}; }
}

/** Az admin által a modulhoz rendelt téma (ha nem döntött: az alapérték). */
export function kijeloltTema(mod: string | null | undefined, nyers: any): ModulTema {
  const m = String(mod || '');
  if (!FOKUSZ_KEPES.has(m)) return 'klasszikus';
  const b = temaBeallitas(nyers)[m];
  if (b === 'klasszikus' || b === 'fokusz') return b;
  return ALAP_TEMA[m] || 'klasszikus';
}

// ── Kipróbálás (csak ebben a böngészőben) ─────────────────────────────────────────────────────
const probaKulcs = (fe: string, mod: string) => `modulTemaProba:${fe}:${mod}`;

export function probaTema(fe: string | null | undefined, mod: string | null | undefined): ModulTema | null {
  if (!fe || !mod) return null;
  try {
    const v = localStorage.getItem(probaKulcs(fe, mod));
    return v === 'klasszikus' || v === 'fokusz' ? v : null;
  } catch { return null; }
}

export function probaBeallit(fe: string, mod: string, tema: ModulTema | null): void {
  try {
    if (tema) localStorage.setItem(probaKulcs(fe, mod), tema);
    else localStorage.removeItem(probaKulcs(fe, mod));
  } catch { /* privát ablak: a próba nem marad meg, a kijelölt téma él */ }
}

/** A modul most érvényes témája: a kipróbált (ha van), különben az admin által kijelölt. */
export function ervenyesTema(fe: string | null | undefined, mod: string | null | undefined, nyers: any): ModulTema {
  if (!FOKUSZ_KEPES.has(String(mod || ''))) return 'klasszikus';
  return probaTema(fe, mod) || kijeloltTema(mod, nyers);
}
